import { StockStatus, type StockStatusT } from '@vs-shop/domain/stock';

import type { DateSerializable, StockInfo } from './stockInfo';

export type { StockInfo } from './stockInfo';

type HasMainImage = {
  readonly mainImage?: { readonly pathAbstract?: string | null | undefined } | null | undefined;
};

type HasCouponCodes = {
  readonly couponCodes?:
    | readonly {
        readonly promotedPrice: number;
        readonly code: string;
        readonly style: 'none' | 'clickable' | 'static';
      }[]
    | undefined;
};

type HasStockFields = {
  readonly available?: boolean | undefined;
  readonly stockStatus?: StockStatusT | undefined;
  readonly maxOrderable?: number | null | undefined;
  readonly dispatchDate?: DateSerializable | null | undefined;
  readonly localWarehouse?:
    | readonly {
        readonly type?: string | null | undefined;
        readonly quantity: number | null;
        readonly dispatchDate?: DateSerializable | null | undefined;
      }[]
    | undefined;
};

export type DiscountBanner = {
  money: number;
  promo: string;
  style: 'none' | 'clickable' | 'static';
};

export function getMainImagePath(product: HasMainImage): string | null {
  const mainImage = product.mainImage ?? null;
  return mainImage !== null ? (mainImage.pathAbstract ?? null) : null;
}

export function getFirstVariant<V>(variants: readonly V[]): V | null {
  return variants[0] ?? null;
}

export function getDiscountBanner(variant: HasCouponCodes | null): DiscountBanner | null {
  if (variant === null) return null;
  const couponCodes = variant.couponCodes ?? [];
  const couponCode = couponCodes[0] ?? null;
  if (couponCode === null) return null;
  return {
    money: couponCode.promotedPrice,
    promo: couponCode.code,
    style: couponCode.style,
  };
}

export function buildVariantMap<V extends { readonly code: string }>(
  variants: readonly V[]
): ReadonlyMap<string, V> {
  return new Map(variants.map((v) => [v.code, v]));
}

export function getStockInfo(variant: HasStockFields | null): StockInfo {
  const localWarehouse = variant?.localWarehouse ?? [];
  return {
    inStock: variant?.available ?? false,
    status: variant?.stockStatus ?? StockStatus.SOLD_OUT,
    eshop: {
      maxOrderable: variant?.maxOrderable === undefined ? 0 : variant.maxOrderable,
      dispatchDate: variant?.dispatchDate ?? null,
    },
    localWarehouse: localWarehouse.map((warehouse) => ({
      type: warehouse.type ?? null,
      quantity: warehouse.quantity ?? 0,
      dispatchDate: warehouse.dispatchDate ?? null,
    })),
  };
}
